import { adminApi, adminApiInZone } from './api-client';
import { fanOutZones, failedZoneLabels, throwIfAllZonesFailed, type PortalZone } from './zones';

/**
 * Route one call to a specific zone (ADR-122). Omitting the zone means HOME, not ANY.
 */
const call = <T>(zone: PortalZone | undefined, path: string, init?: RequestInit): Promise<T> =>
  zone ? adminApiInZone<T>(zone, path, init) : adminApi<T>(path, init);

/**
 * Typed wrappers around the admin-api Portal Access routes (ADR-081 D11 /
 * Multi-Node Authentication Reference §4.3) — operator provisioning for the
 * three operator-portal types:
 *
 *   - GET    /admin/portal-access                    orgs + operator lists
 *   - POST   /admin/portal-access/operators          enrol an operator
 *   - PATCH  /admin/portal-access/operators/{sub}    suspend / reactivate / set_role / set_scope
 *   - DELETE /admin/portal-access/operators/{sub}    remove the attachment
 *
 * Server gate: reads subscribers:read, mutations customers:manage (MUTATING_ROLES
 * + fresh-auth) — the ADR-072 tier. Operators are subscriber-side identities;
 * being an operator is a node attachment (a portal door in the L1 chooser), and
 * NEVER client access — that stays consent-grant-only.
 */

export const PORTAL_TYPES = ['beta', 'consultant', 'partner'] as const;
export type PortalType = (typeof PORTAL_TYPES)[number];

export const PORTAL_TYPE_LABELS: Record<PortalType, string> = {
  beta: 'Beta Management',
  consultant: 'Nexa Consultant',
  partner: 'Partner',
};

/** Management-tier roles (Beta Management / Nexa Consultant orgs). */
export const MANAGEMENT_OPERATOR_ROLES = ['operator_admin', 'operator_staff'] as const;
/** Partner-tier roles (reseller orgs — the existing Cedar partner roles). */
export const PARTNER_OPERATOR_ROLES = ['partner_admin', 'partner_staff', 'partner_view'] as const;

export type PortalOperatorRole =
  | (typeof MANAGEMENT_OPERATOR_ROLES)[number]
  | (typeof PARTNER_OPERATOR_ROLES)[number];

/** Roles assignable per portal type — the UI dropdown is filtered by this;
 *  the server re-validates the pair (defence in depth). */
export const ROLES_BY_PORTAL: Record<PortalType, readonly PortalOperatorRole[]> = {
  beta: MANAGEMENT_OPERATOR_ROLES,
  consultant: MANAGEMENT_OPERATOR_ROLES,
  partner: PARTNER_OPERATOR_ROLES,
};

export const OPERATOR_ROLE_LABELS: Record<string, string> = {
  operator_admin: 'Operator Admin',
  operator_staff: 'Operator Staff',
  partner_admin: 'Partner Admin',
  partner_staff: 'Partner Staff',
  partner_view: 'Partner Viewer',
};

export const OPERATOR_ROLE_DESCRIPTIONS: Record<string, string> = {
  operator_admin: 'Runs the portal and can enter granted client accounts (impersonation).',
  operator_staff: 'Sees the portal’s client book; cannot enter client accounts.',
  partner_admin: 'Full partner-org admin, including entering granted clients.',
  partner_staff: 'Manages clients across the partner org; no impersonation.',
  partner_view: 'Read-only across the partner org.',
};

export function operatorRoleLabel(role: string): string {
  return OPERATOR_ROLE_LABELS[role] ?? (role || '—');
}

/** F6 — consultant book visibility (Reference §4.3): 'assigned' = their own
 *  admin-assigned book (the default); 'full' = a lead across the whole org. */
export type ConsultantVisibility = 'assigned' | 'full';

export interface PortalOperator {
  cognitoSub: string;
  /**
   * ⭐ Cognito activation state — FORCE_CHANGE_PASSWORD until the person sets
   * their password, CONFIRMED after. A DIFFERENT AXIS from `status`, which is
   * suspension. Consultant rows only today (the server enriches those); absent
   * elsewhere, and an absent value renders as the plain suspension status.
   */
  cognitoStatus?: string;
  email: string;
  displayName: string;
  role: string;
  status: 'active' | 'suspended';
  scopePartnerOrgId?: string;
  /** F6 — consultant portal only; absent means 'assigned' (the default). */
  visibility?: ConsultantVisibility;
  grantedAt: string;
  grantedBy: string;
  /** Cognito sign-in enablement (null when the lookup failed). */
  enabled: boolean | null;
}

export interface PortalOrg {
  /**
   * The zone this org row was read from — tagged by the fan-out (ADR-122 Pattern 1).
   *
   * ⭐ THE SAME `orgId` EXISTS ONCE PER ZONE (Jeff, 23 September 2026 — operator orgs exist
   * in every zone), so `orgId` alone no longer names an org: `nexa-consultant-programme` in
   * Sydney and in Virginia are two orgs with two memberships. `(zone, orgId)` is the key, and
   * every write goes back to this zone.
   */
  zone?: string;
  portalType: PortalType;
  orgId: string;
  name: string;
  /** false for a singleton org not yet seeded (created on first enrolment). */
  exists: boolean;
  operators: PortalOperator[];
}

/** One zone's orgs and operators. Omitted zone = the home zone. */
export async function listPortalAccess(zone?: PortalZone): Promise<PortalOrg[]> {
  const resp = await call<{ portals: PortalOrg[] }>(zone, '/admin/portal-access');
  return resp.portals;
}

/**
 * Every zone's orgs and operators (ADR-122 Pattern 1) — what the Dashboard Access tab shows.
 *
 * Each org is tagged with the zone it was read from, and each org stays its OWN row: the two
 * zones' `nexa-consultant-programme` orgs are never merged, because they are two orgs whose
 * members live in two jurisdictions. A zone that did not answer is returned in `failedZones`
 * rather than silently shortening the list.
 */
export async function listAllPortalAccess(): Promise<{ portals: PortalOrg[]; failedZones: string[] }> {
  const results = await fanOutZones(async (zone: PortalZone) =>
    (await listPortalAccess(zone)).map((org) => ({ ...org, zone: zone.code })),
  );
  // No zone answered → a failed read, not "no operators anywhere".
  throwIfAllZonesFailed(results);
  return { portals: results.flatMap((r) => r.items), failedZones: failedZoneLabels(results) };
}

export interface AddOperatorInput {
  portalType: PortalType;
  /**
   * ADR-124 D4 — the appointee's OWN country. Required by the server for a consultant (the only
   * live arm): appointing one creates a home for a person, and its zone follows the country.
   */
  jurisdiction?: string;
  /** Required for partner; server defaults the singleton for beta/consultant. */
  orgId?: string;
  email: string;
  displayName?: string;
  /**
   * ⭐ OPTIONAL BECAUSE A CONSULTANT HAS NO ROLE (ADR-116 D-C5). Omitted for the
   * consultant portal — `appointConsultant()` takes no role, so a value here
   * would be invented purely to satisfy a field the server discards. Still
   * required in practice for any portal that has real, distinct roles.
   *
   * ⛔ NO `visibility` FIELD. It used to sit here for the consultant portal
   * ('assigned' vs 'full'); the server destructured it and never read it, and
   * the matching PATCH op is refused outright (D-C4). Removing it from the type
   * is what stops it being sent again by accident.
   */
  role?: PortalOperatorRole;
  /** Beta only — confine the book to this partner org's clients. */
  scopePartnerOrgId?: string;
}

/**
 * ⭐ `zone` IS REQUIRED ON EVERY WRITE IN THIS MODULE — not optional-meaning-home, as the reads
 * are. The operator's org exists once per zone, so a write with no zone is not "the default
 * zone", it is a guess; before this change every enrolment landed in Australia whatever the
 * console showed. For a NEW consultant the zone is also where their sign-in is born (that
 * zone's subscriber pool), so it comes from the form's create-target control.
 */
export function addPortalOperator(input: AddOperatorInput, zone: PortalZone): Promise<{
  created: boolean;
  cognitoSub: string;
  email: string;
  portalType: PortalType;
  orgId: string;
  role: string;
  createdIdentity: boolean;
  emailSent?: boolean;
}> {
  return call(zone, '/admin/portal-access/operators', {
    method: 'POST',
    body: JSON.stringify({
      portalType: input.portalType,
      ...(input.orgId ? { orgId: input.orgId } : {}),
      email: input.email,
      ...(input.displayName ? { displayName: input.displayName } : {}),
      // Conditional, not `role: input.role`. JSON.stringify would drop an
      // undefined value anyway, but relying on that hides the intent — and the
      // point of this change is that the field is ABSENT for consultant, not
      // merely empty.
      ...(input.role ? { role: input.role } : {}),
      ...(input.scopePartnerOrgId ? { scopePartnerOrgId: input.scopePartnerOrgId } : {}),
      ...(input.jurisdiction ? { jurisdiction: input.jurisdiction } : {}),
    }),
  });
}

export type OperatorLifecycleOp = 'suspend' | 'reactivate' | 'set_role' | 'set_scope' | 'set_visibility';

export function updatePortalOperator(
  cognitoSub: string,
  input: {
    portalType: PortalType;
    orgId: string;
    op: OperatorLifecycleOp;
    role?: PortalOperatorRole;
    /** For set_scope: a partner orgId, or null to clear back to full visibility. */
    scopePartnerOrgId?: string | null;
    /** For set_visibility (consultant portal only). */
    visibility?: ConsultantVisibility;
  },
  /** The ORG's zone — the row being changed lives there and nowhere else. */
  zone: PortalZone,
): Promise<{ ok: boolean; op: OperatorLifecycleOp }> {
  return call(zone, `/admin/portal-access/operators/${encodeURIComponent(cognitoSub)}`, {
    method: 'PATCH',
    body: JSON.stringify({
      portalType: input.portalType,
      orgId: input.orgId,
      op: input.op,
      ...(input.role ? { role: input.role } : {}),
      ...(input.scopePartnerOrgId !== undefined ? { scopePartnerOrgId: input.scopePartnerOrgId } : {}),
      ...(input.visibility ? { visibility: input.visibility } : {}),
    }),
  });
}

// ─── F6 (Reference §4.3) — consultant customer assignment ────────────────────

export interface ConsultantAssignment {
  clientAccountId: string;
  assignedAt: string;
  assignedBy: string;
}

export async function listConsultantAssignments(
  cognitoSub: string,
  orgId: string,
  /** The zone to read. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<ConsultantAssignment[]> {
  const resp = await call<{ assignments: ConsultantAssignment[] }>(zone,
    `/admin/portal-access/operators/${encodeURIComponent(cognitoSub)}/assignments?orgId=${encodeURIComponent(orgId)}`,
  );
  return resp.assignments;
}

/**
 * Every customer assigned to this consultant, across every zone (ADR-122).
 *
 * ⭐⭐ THIS ONE IS NOT COSMETIC, AND IT IS NOT WHAT ADR-122 EXPECTED TO FIND HERE. The ADR
 * describes `AssignCustomersPage` as calling customers-api's non-fan-out path; it does not —
 * it already calls `listAllCustomers`, so the customer LIST was already merged across zones.
 * The assignments beside it were not, and that combination is strictly worse than either being
 * consistently single-zone:
 *
 *   an assignment is keyed by `clientAccountId`, which lives in the CUSTOMER'S zone. So a
 *   us-east-1 customer appeared in the merged list with its "Assigned" box read from AU, where
 *   that assignment cannot exist. Every US customer therefore rendered as UNASSIGNED however
 *   many times it had been assigned — and ticking the box wrote the assignment to AU, against
 *   an account that is not there. The operator sees a checkbox that will not stay ticked.
 *
 * The list merge is what created the defect: it put rows on screen whose companion state was
 * being read from the wrong place. Fixing the list without fixing this would have left the
 * page confidently wrong rather than merely incomplete.
 */
export async function listAllConsultantAssignments(
  cognitoSub: string,
  orgId: string,
): Promise<{ assignments: ConsultantAssignment[]; failedZones: string[] }> {
  const results = await fanOutZones((zone) => listConsultantAssignments(cognitoSub, orgId, zone));
  // No zone answered → a failed read. Absorbing it would render every customer as unassigned.
  throwIfAllZonesFailed(results);
  return {
    assignments: results.flatMap((r) => r.items),
    failedZones: failedZoneLabels(results),
  };
}

/**
 * ⛔⛔ CONSULTANTS SERVE CUSTOMERS IN THEIR OWN DATA REGION ONLY — commercial policy (Jeff,
 * 26 September 2026; ADR-116 Status note). Both calls below are therefore addressed to the
 * CONSULTANT ORG's zone — the only zone the consultant exists in — and carry `accountZone`,
 * the zone the customer row was READ from. The server compares that tag with its own zone
 * before any lookup and refuses a mismatch as `403 cross_zone_assignment`, a stated policy
 * refusal rather than the misleading `operator_not_found` a cross-zone write used to die on.
 *
 * ⚠ BOTH ARGUMENTS ARE REQUIRED, ON PURPOSE. An omitted zone would silently mean home, and
 * an omitted tag would turn the policy refusal back into "not found". Pinned by
 * `scripts/test/zone-routing-contract.test.mjs` contract 7.
 */
export function assignConsultantCustomer(
  cognitoSub: string,
  orgId: string,
  clientAccountId: string,
  /** The consultant ORG's zone — where the consultant exists and the assignment is written. */
  zone: PortalZone,
  /** The zone the customer row was read from (its fan-out tag). */
  accountZone: string,
): Promise<{ ok: boolean; assigned: boolean; already: boolean }> {
  return call(zone,
    `/admin/portal-access/operators/${encodeURIComponent(cognitoSub)}/assignments/${encodeURIComponent(clientAccountId)}`,
    { method: 'PUT', body: JSON.stringify({ orgId, accountZone }) },
  );
}

export function unassignConsultantCustomer(
  cognitoSub: string,
  orgId: string,
  clientAccountId: string,
  /** The consultant ORG's zone — where the assignment lives. */
  zone: PortalZone,
  /** The zone the customer row was read from (its fan-out tag). */
  accountZone: string,
): Promise<{ ok: boolean }> {
  return call(zone,
    `/admin/portal-access/operators/${encodeURIComponent(cognitoSub)}/assignments/${encodeURIComponent(clientAccountId)}`
      + `?orgId=${encodeURIComponent(orgId)}&accountZone=${encodeURIComponent(accountZone)}`,
    { method: 'DELETE' },
  );
}

export function removePortalOperator(
  cognitoSub: string,
  portalType: PortalType,
  orgId: string,
  /** The ORG's zone — the row being removed lives there and nowhere else. */
  zone: PortalZone,
): Promise<{ ok: boolean }> {
  const qs = `?portalType=${encodeURIComponent(portalType)}&orgId=${encodeURIComponent(orgId)}`;
  return call(zone, `/admin/portal-access/operators/${encodeURIComponent(cognitoSub)}${qs}`, {
    method: 'DELETE',
  });
}
